import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  FloatingLabel,
  Form,
  Image,
  Row,
  Table,
} from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FaImages } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCorners,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ActionButton from "components/common/ActionButton";
import FalconLightBoxGallery from "components/common/FalconLightBoxGallery";
import SubtleBadge from "components/common/SubtleBadge";
import axios from "axios";
import LightBoxLatestExample from "components/common/LightBoxLatestExample";
import { BASEURL } from "utils/customConfig";
import { IoIosArrowDown, IoIosArrowForward } from "react-icons/io";

const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td
        {...listeners}
        {...attributes}
        style={{
          cursor: "grab",
          textAlign: "center",
          width: "10px",
          paddingTop: "15px",
        }}
      >
        ☰
      </td>
      {children}
    </tr>
  );
};

const SortableTag = ({ tag, tagIndex, rowIndex, handleRemoveTag }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: tag }); // unique id for DnD

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: "inline-flex",
    alignItems: "center",
    margin: "0 4px 4px 0",
    color: "#215cac",
    backgroundColor: "#d9e7fa", // subtle badge color
    paddingRight: "4px",
    borderRadius: "4px",
  };

  return (
    <div style={style}>
      {/* Draggable area */}
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        style={{
          cursor: "grab",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        <SubtleBadge className="mr-0">{tag}</SubtleBadge>
      </div>

      {/* Remove button outside draggable area */}
      <span
        style={{ cursor: "pointer", marginLeft: "0px", fontWeight: "bold" }}
        onClick={(e) => {
          e.stopPropagation(); // prevent triggering drag
          handleRemoveTag(rowIndex, tag);
        }}
      >
        &times;
      </span>
    </div>
  );
};

const Dropzone = ({ onDrop, index }) => {
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
      // "image/gif": [".gif"],
      // "image/webp": [".webp"],
    },
    multiple: true,
    onDrop: (acceptedFiles) => onDrop(acceptedFiles, index),
  });

  return (
    <div {...getRootProps({ className: "dropzone" })}>
      <input {...getInputProps()} />
      <div style={{ padding: "5px" }}>
        <FaImages size={25} />
      </div>
    </div>
  );
};

const VariantsBasicInformation = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [inputValues, setInputValues] = useState({});
  const [optionsList, setOptionsList] = useState({});
  const [imagesList, setImagesList] = useState({});
  const [imagesPreviewList, setImagesPreviewList] = useState({});
  const [serverImagesList, setServerImagesList] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  console.log("optionsList", optionsList);
  console.log("inputValues", inputValues);

  const {
    register,
    setValue,
    watch,
    control,
    getValues,
    formState: { errors },
  } = useFormContext();

  const nameWatch = watch("name");
  const maxCharactersLength = 255;

  const getTagsData = () => Object.values(optionsList);

  // const useTagValidation = (optionsList) => {
  //   const { setError, clearErrors } = useFormContext();

  //   useEffect(() => {
  //     Object.keys(optionsList).forEach((rowIndex) => {
  //       const tags = optionsList[rowIndex] || [];

  //       // Check if any tag is empty or whitespace only
  //       const hasEmpty = tags.some((t) => !t || t.trim() === "");
  //       console.log("Has Empty", hasEmpty);

  //       if (hasEmpty) {
  //         // setError(`options.${rowIndex}.options`, {
  //         //   type: "manual",
  //         //   message: "Tags cannot be empty",
  //         // });
  //       } else {
  //         clearErrors(`options.${rowIndex}.options`);
  //       }
  //     });
  //   }, [optionsList, setError, clearErrors]);
  // };

  // useTagValidation(optionsList);

  const {
    fields: options,
    append: appendOptions,
    remove: removeOptions,
    move,
  } = useFieldArray({
    control,
    name: "options",
  });

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  const uploadImages = async (files) => {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append("files", file); // 'files[]' is the key name on the backend
    });

    try {
      const response = await axios.post("/Catalogue/VariantFiles", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Assuming your backend returns a list of URLs
      return response?.data;
    } catch (error) {
      console.error("Error uploading images:", error);
      return [];
    }
  };

  // const handleDrop = useCallback(
  //   async (acceptedFiles, index) => {
  //     // Create object URLs for preview
  //     const newImages = acceptedFiles.map((file) => URL.createObjectURL(file));

  //     // Update image preview list
  //     setImagesPreviewList((prevImagesList) => {
  //       const updatedImagesPreview = {
  //         ...prevImagesList,
  //         [index]: [...(prevImagesList[index] || []), ...newImages],
  //       };

  //       setValue(
  //         `options[${index}].optionImagesPreview`,
  //         updatedImagesPreview[index],
  //       );

  //       return updatedImagesPreview;
  //     });

  //     // Upload images and get the URLs
  //     const response = await uploadImages(acceptedFiles);

  //     // Filter out null values from response
  //     const destinations = response
  //       ?.map((item) => item.destination)
  //       .filter((url) => url !== null);

  //     // Update image list
  //     setImagesList((prevImagesList) => {
  //       const updatedImagesList = { ...prevImagesList };

  //       // Ensure the array at index is initialized
  //       updatedImagesList[index] = [
  //         ...(updatedImagesList[index] || []),
  //         ...destinations,
  //       ];

  //       setValue(`options[${index}].optionImages`, updatedImagesList[index]);

  //       // console.log(getValues(`options[${index}].optionImages`));
  //       return updatedImagesList;
  //     });
  //   },
  //   [setValue, uploadImages, getValues],
  // );

  const handleDrop = useCallback(
    async (acceptedFiles, index) => {
      // Upload first
      const response = await uploadImages(acceptedFiles);

      const destinations = response
        ?.map((item) => item.destination)
        .filter((url) => url !== null);

      setImagesList((prev) => {
        const existing =
          prev[index] || getValues(`options[${index}].optionImages`) || [];

        const merged = [...existing, ...destinations];

        const updated = {
          ...prev,
          [index]: merged,
        };

        // 🔥 This is what actually saves
        setValue(`options[${index}].optionImages`, merged);

        return updated;
      });
    },
    [setValue, uploadImages, getValues],
  );

  const removeImage = (rowIndex, imageIndex) => {
    setImagesPreviewList((prevPreviewList) => {
      if (!prevPreviewList[rowIndex]) return prevPreviewList;

      const updatedPreview = [...prevPreviewList[rowIndex]];
      updatedPreview.splice(imageIndex, 1);

      setValue(`options[${rowIndex}].optionImagesPreview`, updatedPreview);

      return {
        ...prevPreviewList,
        [rowIndex]: updatedPreview,
      };
    });

    setImagesList((prevImagesList) => {
      if (!prevImagesList[rowIndex]) return prevImagesList;

      const updatedImages = [...prevImagesList[rowIndex]];
      const removedImage = updatedImages[imageIndex];

      updatedImages.splice(imageIndex, 1);

      // Update form value
      setValue(`options[${rowIndex}].optionImages`, updatedImages);

      // 🔥 CLEAN optionImageMap IF IMAGE WAS LINKED
      const currentMap = getValues(`options[${rowIndex}].optionImageMap`) || {};

      const cleanedMap = {};
      Object.keys(currentMap).forEach((key) => {
        cleanedMap[key] =
          currentMap[key] === removedImage ? "" : currentMap[key];
      });

      setValue(`options[${rowIndex}].optionImageMap`, cleanedMap);

      return {
        ...prevImagesList,
        [rowIndex]: updatedImages,
      };
    });
  };

  // const handleDragEnd = (event) => {
  //   const { active, over } = event;

  //   if (over && active.id !== over.id) {
  //     const oldIndex = options.findIndex((item) => item.id === active.id);
  //     const newIndex = options.findIndex((item) => item.id === over.id);

  //     if (oldIndex !== -1 && newIndex !== -1) {
  //       move(oldIndex, newIndex);

  //       const updatedOptionsList = { ...optionsList };
  //       const movedItemOptions = updatedOptionsList[oldIndex];
  //       if (oldIndex < newIndex) {
  //         for (let i = oldIndex; i < newIndex; i++) {
  //           updatedOptionsList[i] = updatedOptionsList[i + 1];
  //         }
  //       } else {
  //         for (let i = oldIndex; i > newIndex; i--) {
  //           updatedOptionsList[i] = updatedOptionsList[i - 1];
  //         }
  //       }
  //       updatedOptionsList[newIndex] = movedItemOptions;

  //       const updatedImagesList = { ...imagesList };
  //       const movedItemImages = updatedImagesList[oldIndex];
  //       if (oldIndex < newIndex) {
  //         for (let i = oldIndex; i < newIndex; i++) {
  //           updatedImagesList[i] = updatedImagesList[i + 1];
  //         }
  //       } else {
  //         for (let i = oldIndex; i > newIndex; i--) {
  //           updatedImagesList[i] = updatedImagesList[i - 1];
  //         }
  //       }
  //       updatedImagesList[newIndex] = movedItemImages;

  //       setOptionsList(updatedOptionsList);
  //       setImagesList(updatedImagesList);
  //     }
  //   }
  // };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = options.findIndex((item) => item.id === active.id);
    const newIndex = options.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Move option row
    move(oldIndex, newIndex);

    const moveObjectIndex = (obj) => {
      const temp = { ...obj };
      const movedItem = temp[oldIndex];
      if (oldIndex < newIndex) {
        for (let i = oldIndex; i < newIndex; i++) {
          temp[i] = temp[i + 1];
        }
      } else {
        for (let i = oldIndex; i > newIndex; i--) {
          temp[i] = temp[i - 1];
        }
      }
      temp[newIndex] = movedItem;
      return temp;
    };

    setOptionsList((prev) => moveObjectIndex(prev));
    setImagesList((prev) => moveObjectIndex(prev));
    setImagesPreviewList((prev) => moveObjectIndex(prev));
    setServerImagesList((prev) => moveObjectIndex(prev));
  };

  // const handleKeyPress = (index, e) => {
  //   if (e.key === "Enter" && inputValues[index]) {
  //     e.preventDefault();
  //     const trimmedValue = inputValues[index].trim();
  //     if (!optionsList[index]?.includes(trimmedValue)) {
  //       const updatedOptions = [...(optionsList[index] || []), trimmedValue];
  //       setOptionsList((prevOptions) => ({
  //         ...prevOptions,
  //         [index]: updatedOptions,
  //       }));
  //       setValue(`options[${index}].options`, updatedOptions);
  //       syncOptionImageMap(index, updatedOptions);
  //     }
  //     setInputValues((prevInputValues) => ({
  //       ...prevInputValues,
  //       [index]: "",
  //     }));
  //   }
  // };

  const handleKeyPress = (index, e) => {
    if (e.key !== "Enter") return;

    e.preventDefault();

    const rawValue = inputValues[index] || "";
    const trimmedValue = rawValue.trim();

    // ❌ Block empty or spaces only
    if (!trimmedValue) return;

    // ❌ Block special characters
    const validPattern = /^[a-zA-Z0-9 ]+$/;

    if (!validPattern.test(trimmedValue)) {
      return; // or show error toast if you want
    }

    // ❌ Prevent duplicate
    if (optionsList[index]?.includes(trimmedValue)) return;

    const updatedOptions = [...(optionsList[index] || []), trimmedValue];

    setOptionsList((prev) => ({
      ...prev,
      [index]: updatedOptions,
    }));

    setValue(`options[${index}].options`, updatedOptions);
    syncOptionImageMap(index, updatedOptions);

    // Clear input
    setInputValues((prev) => ({
      ...prev,
      [index]: "",
    }));
  };

  const handleRemoveTag = (index, tagToRemove) => {
    const updatedOptions = optionsList[index]?.filter(
      (tag) => tag !== tagToRemove,
    );
    setOptionsList((prevOptions) => ({
      ...prevOptions,
      [index]: updatedOptions,
    }));
    setValue(`options[${index}].options`, updatedOptions);
    syncOptionImageMap(index, updatedOptions);
  };

  const handleInputChange = (index, value) => {
    setInputValues((prevInputValues) => ({
      ...prevInputValues,
      [index]: value,
    }));
  };

  const handleRemoveRow = (index) => {
    // Remove the row from the form state using removeOptions
    removeOptions(index);

    // Update optionsList state
    setOptionsList((prevOptionsList) => {
      const updatedOptionsList = { ...prevOptionsList };
      delete updatedOptionsList[index];

      // Adjust the remaining indexes
      const newOptionsList = Object.keys(updatedOptionsList).reduce(
        (acc, key) => {
          const newKey = key > index ? key - 1 : key;
          acc[newKey] = updatedOptionsList[key];
          return acc;
        },
        {},
      );
      return newOptionsList;
    });

    // Update imagesList state
    setImagesList((prevImagesList) => {
      const updatedImagesList = { ...prevImagesList };
      delete updatedImagesList[index];

      // Adjust the remaining indexes
      const newImagesList = Object.keys(updatedImagesList).reduce(
        (acc, key) => {
          const newKey = key > index ? key - 1 : key;
          acc[newKey] = updatedImagesList[key];
          return acc;
        },
        {},
      );
      return newImagesList;
    });

    // Update inputValues state
    setInputValues((prevInputValues) => {
      const updatedInputValues = { ...prevInputValues };
      delete updatedInputValues[index];

      // Adjust the remaining indexes
      const newInputValues = Object.keys(updatedInputValues).reduce(
        (acc, key) => {
          const newKey = key > index ? key - 1 : key;
          acc[newKey] = updatedInputValues[key];
          return acc;
        },
        {},
      );
      return newInputValues;
    });
  };

  // console.log(state);

  const handleTagDragEnd = (event, rowIndex) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setOptionsList((prevOptionsList) => {
      const tags = [...(prevOptionsList[rowIndex] || [])];
      const oldIndex = tags.indexOf(active.id);
      const newIndex = tags.indexOf(over.id);

      if (oldIndex === -1 || newIndex === -1) return prevOptionsList;

      const [movedTag] = tags.splice(oldIndex, 1);
      tags.splice(newIndex, 0, movedTag);

      const updatedOptionsList = { ...prevOptionsList, [rowIndex]: tags };
      setValue(`options[${rowIndex}].options`, tags);

      const currentMap = getValues(`options[${rowIndex}].optionImageMap`) || {};

      const reorderedMap = {};
      tags.forEach((tag) => {
        reorderedMap[tag] = currentMap[tag] || "";
      });

      setValue(`options[${rowIndex}].optionImageMap`, reorderedMap);

      return updatedOptionsList;
    });
  };

  const syncOptionImageMap = (rowIndex, tags) => {
    const current = getValues(`options[${rowIndex}].optionImageMap`) || {};

    const updated = tags.reduce((acc, tag) => {
      acc[tag] = current[tag] || "";
      return acc;
    }, {});

    setValue(`options[${rowIndex}].optionImageMap`, updated);
  };

  useEffect(() => {
    if (state) {
      const { options } = state;

      // Initialize inputValues, optionsList, and imagesList from the state
      const initialInputValues = {};
      const initialOptionsList = {};
      const initialImagesList = {};
      const serverImagesList = {};

      options.forEach((option, index) => {
        initialInputValues[index] = ""; // Assuming inputs are empty initially
        initialOptionsList[index] = option.options || [];
        initialImagesList[index] = option.optionImages || [];
        serverImagesList[index] = option.images || [];

        // Set form values using setValue
        setValue(`options[${index}].options`, option.options);
        setValue(`options[${index}].optionImages`, option.optionImages);
        setValue(`options[${index}].optionImagesPreview`, option.images);
        // setValue(`options[${index}].name`, option.name);
        // setValue(`options[${index}].sortOrder`, option.sortOrder);
      });

      setInputValues(initialInputValues);
      setOptionsList(initialOptionsList);
      setImagesList(initialImagesList);
      setServerImagesList(serverImagesList);
    }
  }, [state, setValue]);

  return (
    <div className="mb-3">
      <Card.Body
        className=""
        style={{
          height: `calc(100vh - 8.4rem)`,
          overflow: "auto",
          padding: "5px 20px",
          // paddingRight: "7px",
        }}
      >
        <Row className="gx-2">
          <Col md="10">
            <Form.Group>
              {/* <FloatingLabel controlId="floatingInput" label="Name"> */}
              <Form.Control
                type="text"
                placeholder="Name"
                title="Name"
                autoFocus
                maxLength={maxCharactersLength}
                className="py-2"
                isInvalid={!!errors.name}
                {...register("name")}
              />
              <div className="d-flex justify-content-between align-items-center">
                <Form.Control.Feedback type="invalid" className="d-inline w-75">
                  {errors.name?.message}
                </Form.Control.Feedback>
                <small className="fs-10 text-muted">
                  {`${nameWatch?.length || 0} / ${maxCharactersLength}`}
                </small>
              </div>
            </Form.Group>
          </Col>
          <Col md="2" style={{ paddingRight: "4px" }}>
            <Form.Check
              type="checkbox"
              id="defaultSwitch"
              label="Active"
              className="mt-2 d-flex justify-content-end gap-1"
              defaultChecked={!state}
              isInvalid={!!errors.status}
              {...register("status")}
            />
          </Col>
        </Row>
        <Row className="gx-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={options.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <Table responsive>
                <thead className="bg-200">
                  <tr>
                    <th scope="col" className="w-0 fs-10 text-center"></th>
                    <th style={{ width: "30px" }}></th>
                    <th
                      scope="col"
                      className="w-0 fs-10 text-center"
                      style={{
                        textAlign: "center",
                        width: "10px",
                      }}
                    >
                      Order
                    </th>
                    <th scope="col" className="fs-10">
                      Name
                    </th>
                    <th scope="col" className="fs-10">
                      Options
                    </th>
                    <th scope="col" className="fs-10 text-center">
                      Show Images
                    </th>
                    <th scope="col" className="fs-10">
                      Images
                    </th>
                    <th scope="col"></th>
                    <th
                      scope="col"
                      style={{ padding: "9px", width: "10px" }}
                      className=" text-end"
                    >
                      <ActionButton
                        icon="plus"
                        variant="success"
                        title="Add"
                        className="px-1 py-0"
                        onClick={() => appendOptions({})}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {options.map((item, index) => (
                    <>
                      <SortableItem key={item.id} id={item.id}>
                        <td style={{ padding: "10px" }} className="text-center">
                          <Button
                            size="sm"
                            variant="light"
                            onClick={() =>
                              setExpandedRows((prev) => ({
                                ...prev,
                                [index]: !prev[index],
                              }))
                            }
                          >
                            {expandedRows[index] ? (
                              <IoIosArrowForward size={20} />
                            ) : (
                              <IoIosArrowDown size={20} />
                            )}
                          </Button>
                        </td>

                        <td style={{ padding: "13px" }} className="text-center">
                          {index + 1}
                        </td>

                        <td style={{ padding: "10px" }}>
                          <Form.Control
                            type="text"
                            placeholder="Name"
                            isInvalid={!!errors.options?.[index]?.name}
                            {...register(`options[${index}].name`)}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.options?.[index]?.name?.message}
                          </Form.Control.Feedback>
                        </td>

                        <td style={{ width: "30%", padding: "10px" }}>
                          <DndContext
                            onDragEnd={(event) =>
                              handleTagDragEnd(event, index)
                            }
                          >
                            <SortableContext
                              items={optionsList[index] || []} // tags array for this row
                            >
                              <div
                                className="form-control d-flex flex-wrap align-items-center"
                                style={{
                                  border: errors.options?.[index]?.options
                                    ? "1px solid red" // red border on error
                                    : "1px solid #ced4da", // default bootstrap border
                                  borderRadius: "4px",
                                  minHeight: "38px",
                                  padding: "5px",
                                }}
                              >
                                {(optionsList[index] || [])
                                  .filter((tag) => tag && tag.trim() !== "") // ✅ filter out empty or space-only tags
                                  .map((tag, tagIndex) => (
                                    <SortableTag
                                      key={tag}
                                      tag={tag}
                                      index={tagIndex}
                                      rowIndex={index}
                                      handleRemoveTag={handleRemoveTag}
                                    />
                                  ))}
                                <input
                                  type="text"
                                  className="border-0 flex-grow-1"
                                  style={{
                                    outline: "none",
                                    color: "black",
                                    padding: "0",
                                  }}
                                  value={inputValues[index] || ""}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      e.target.value || "",
                                    )
                                  }
                                  onKeyPress={(e) => handleKeyPress(index, e)}
                                  placeholder="Tags"
                                />
                              </div>
                            </SortableContext>
                          </DndContext>
                          {errors.options?.[index]?.options && (
                            <div
                              className="text-danger"
                              style={{ fontSize: "0.8rem" }}
                            >
                              {errors.options[index].options.message}
                            </div>
                          )}
                        </td>

                        <td style={{ padding: "5px" }} className="text-center">
                          <Form.Check
                            type="checkbox"
                            id="defaultSwitch"
                            className="mt-3"
                            defaultChecked={!state}
                            isInvalid={!!errors.options?.[index]?.showImage}
                            {...register(`options[${index}].showImage`)}
                          />
                        </td>

                        <td style={{ padding: "10px" }}>
                          <Dropzone onDrop={handleDrop} index={index} />
                        </td>
                        <td style={{ width: "30%", padding: "10px" }}>
                          {/* <MyImageGallery
                            index={index}
                            imagesPreviewList={imagesPreviewList}
                            serverImagesList={serverImagesList}
                            removeImage={removeImage}
                          /> */}
                          <MyImageGallery
                            index={index}
                            imagesPreviewList={imagesPreviewList}
                            imagesList={imagesList}
                            removeImage={removeImage}
                          />
                        </td>
                        <td style={{ padding: "10px" }} className="text-center">
                          <ActionButton
                            icon="trash-alt"
                            title="Delete"
                            variant="danger"
                            className="px-1 py-0"
                            onClick={() => handleRemoveRow(index)}
                          />
                        </td>
                      </SortableItem>

                      {expandedRows[index] && (
                        <tr>
                          <td colSpan={8} style={{ background: "#f8f9fa" }}>
                            <Table size="sm" bordered className="mb-0">
                              <thead>
                                <tr>
                                  <th style={{ width: "25%" }}>Option</th>
                                  <th style={{ width: "40%" }}>Link Image</th>
                                  <th style={{ width: "30%" }}>Preview</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(optionsList[index] || [])
                                  .filter((opt) => opt && opt.trim() !== "") // ✅ skip empty options
                                  .map((opt) => {
                                    const map =
                                      watch(
                                        `options[${index}].optionImageMap`,
                                      ) || {};

                                    const images = (
                                      imagesList[index] || []
                                    ).filter(
                                      (img) =>
                                        img &&
                                        img !== "" &&
                                        img.destination !== "null",
                                    );

                                    const previewImages = (
                                      imagesPreviewList[index] || []
                                    ).filter((img) => img && img !== "");

                                    const combinedImages =
                                      previewImages.length > 0
                                        ? previewImages
                                        : images;

                                    return (
                                      <tr key={opt}>
                                        <td>{opt}</td>

                                        <td>
                                          <Form.Select
                                            value={map[opt] || ""}
                                            onChange={(e) =>
                                              setValue(
                                                `options[${index}].optionImageMap.${opt}`,
                                                e.target.value,
                                              )
                                            }
                                          >
                                            <option value="">
                                              Select Image
                                            </option>
                                            {combinedImages.map((img, i) => (
                                              <option key={i} value={img}>
                                                Image {i + 1}
                                              </option>
                                            ))}
                                          </Form.Select>
                                        </td>

                                        <td>
                                          {map[opt] &&
                                            combinedImages.includes(
                                              map[opt],
                                            ) && (
                                              <Image
                                                src={`${BASEURL}/wwwroot${map[
                                                  opt
                                                ].replace(/\\/g, "/")}`}
                                                width={85}
                                                height={40}
                                                rounded
                                              />
                                            )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </Table>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </Table>
            </SortableContext>
          </DndContext>
        </Row>
        {/* <pre className="mt-3">{JSON.stringify(watch(), null, 2)}</pre> */}
      </Card.Body>
    </div>
  );
};

const MyImageGallery = ({
  index,
  imagesPreviewList,
  imagesList,
  removeImage,
}) => {
  // const previewImages = imagesPreviewList[index] || [];

  // const backendImages = (imagesList[index] || []).map((img) =>
  //   img.startsWith("blob:")
  //     ? img
  //     : `${BASEURL}/wwwroot${img.replace(/\\/g, "/")}`,
  // );

  // // const combinedImages =
  // //   previewImages.length > 0 ? previewImages : backendImages;

  // const combinedImages = [...backendImages, ...previewImages];

  // const combinedImages = (imagesList[index] || [])
  //   .filter((img) => img && img.trim() !== "")
  //   .map((img) =>
  //     img.startsWith("blob:")
  //       ? img
  //       : `${BASEURL}/wwwroot${img.replace(/\\/g, "/")}`,
  //   );

  const combinedImages = (imagesList[index] || [])
    .map((img) => {
      if (!img) return null;

      // Blob preview
      if (img.startsWith("blob:")) return img;

      // Already full URL
      if (img.startsWith("http")) return img;

      // Otherwise treat as backend path
      const cleanedPath = img.replace(/\\/g, "/").replace(/^\/+/, "");

      return `${BASEURL}/wwwroot/${cleanedPath}`;
    })
    .filter(Boolean);

  // console.log("combinedImages", combinedImages);

  // const combinedImages = (imagesList[index] || [])
  //   .filter((img) => img && img.trim() !== "") // remove empty/null
  //   .map((img) => {
  //     // If blob preview
  //     if (img.startsWith("blob:")) return img;

  //     // If already full URL (http/https)
  //     if (img.startsWith("https")) return img;

  //     // If valid backend path
  //     if (img.startsWith("/")) {
  //       return `${BASEURL}/wwwroot${img.replace(/\\/g, "/")}`;
  //     }

  //     return ""; // fallback
  //   })
  //   .filter(Boolean); // remove empty results

  // console.log("combinedImages", combinedImages);

  return (
    <LightBoxLatestExample images={combinedImages}>
      {(setImgIndex) => (
        <Row className="g-2">
          {combinedImages.map((image, imgIndex) => (
            <Col
              xs={4}
              md={2}
              key={imgIndex}
              className="position-relative text-center"
            >
              <Image
                src={image}
                height={50}
                width="100%"
                rounded
                className="cursor-pointer"
                onClick={() => setImgIndex(imgIndex)}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />

              <div style={{ fontSize: "10px", marginTop: "2px" }}>
                Image {imgIndex + 1}
              </div>

              <Button
                variant="danger"
                size="sm"
                onClick={() => removeImage(index, imgIndex)}
                className="position-absolute top-0 end-0"
                style={{ padding: "0px 1px" }}
              >
                &times;
              </Button>
            </Col>
          ))}
        </Row>
      )}
    </LightBoxLatestExample>
  );
};

export default VariantsBasicInformation;

// const MyImageGallery = ({
//   index,
//   imagesPreviewList,
//   serverImagesList,
//   removeImage,
// }) => {
//   // Combine the preview images and server images
//   const combinedImages = [
//     ...(imagesPreviewList[index] || []),
//     // ...(serverImagesList[index]?.map(
//     //   (image) => `data:image/png;base64,${image?.fileBase64}`
//     // ) || []),
//   ];

//   console.log("serverImagesList", serverImagesList);
//   // console.log("imagesPreviewList", imagesPreviewList);
//   console.log("combinedImages", combinedImages);

//   return (
//     <LightBoxLatestExample images={combinedImages}>
//       {(setImgIndex) => (
//         <Row className="g-2">
//           {combinedImages.map((image, imgIndex) => (
//             <Col
//               xs={4}
//               md={2}
//               key={imgIndex}
//               className="position-relative text-center"
//             >
//               <Image
//                 src={image}
//                 height={50}
//                 width="100%"
//                 rounded
//                 className="cursor-pointer"
//                 onClick={() => setImgIndex(imgIndex)}
//               />

//               {/* Show image name / number */}
//               <div style={{ fontSize: "10px", marginTop: "2px" }}>
//                 Image {imgIndex + 1}
//               </div>

//               <Button
//                 variant="danger"
//                 size="sm"
//                 onClick={() => removeImage(index, imgIndex)}
//                 className="position-absolute top-0 end-0"
//                 style={{ padding: "0px 1px 0px 1px" }}
//               >
//                 &times;
//               </Button>
//             </Col>
//           ))}
//         </Row>
//       )}
//     </LightBoxLatestExample>
//   );
// };

// import React, { useCallback, useEffect, useState } from "react";
// import { Button, Card, Col, Form, Image, Row, Table } from "react-bootstrap";
// import { useDropzone } from "react-dropzone";
// import { useFieldArray, useForm, FormProvider } from "react-hook-form";
// import { FaImages } from "react-icons/fa";

// /* ---------------- DROPZONE ---------------- */

// const Dropzone = ({ onDrop, index }) => {
//   const { getRootProps, getInputProps } = useDropzone({
//     accept: { "image/*": [] },
//     multiple: true,
//     onDrop: (files) => onDrop(files, index),
//   });

//   return (
//     <div {...getRootProps()} style={{ cursor: "pointer" }}>
//       <input {...getInputProps()} />
//       <FaImages size={22} />
//     </div>
//   );
// };

// /* ---------------- MAIN COMPONENT ---------------- */

// const VariantsBasicInformation = () => {
//   const methods = useForm({
//     defaultValues: {
//       options: [],
//     },
//   });

//   const { control, register, setValue, watch, getValues } = methods;

//   const { fields, append, remove } = useFieldArray({
//     control,
//     name: "options",
//   });

//   // your existing states style
//   const [inputValues, setInputValues] = useState({});
//   const [optionsList, setOptionsList] = useState({});
//   const [imagesPreviewList, setImagesPreviewList] = useState({});
//   const [expandedRows, setExpandedRows] = useState({});

//   /* ---------------- EXPAND / COLLAPSE ---------------- */

//   const toggleRow = (index) => {
//     setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
//   };

//   /* ---------------- SYNC OPTION -> IMAGE MAP ---------------- */

//   const syncOptionImageMap = (rowIndex, tags) => {
//     const currentMap = getValues(`options[${rowIndex}].optionImageMap`) || {};

//     const newMap = tags.reduce((acc, tag) => {
//       acc[tag] = currentMap[tag] || "";
//       return acc;
//     }, {});

//     setValue(`options[${rowIndex}].optionImageMap`, newMap);
//   };

//   /* ---------------- TAG ADD ---------------- */

//   const handleKeyPress = (index, e) => {
//     if (e.key === "Enter" && inputValues[index]) {
//       e.preventDefault();
//       const trimmedValue = inputValues[index].trim();

//       if (!optionsList[index]?.includes(trimmedValue)) {
//         const updatedOptions = [...(optionsList[index] || []), trimmedValue];

//         setOptionsList((prev) => ({
//           ...prev,
//           [index]: updatedOptions,
//         }));

//         setValue(`options[${index}].options`, updatedOptions);

//         syncOptionImageMap(index, updatedOptions);
//       }

//       setInputValues((prev) => ({ ...prev, [index]: "" }));
//     }
//   };

//   /* ---------------- TAG REMOVE ---------------- */

//   const handleRemoveTag = (index, tag) => {
//     const updatedOptions = optionsList[index]?.filter((t) => t !== tag);

//     setOptionsList((prev) => ({
//       ...prev,
//       [index]: updatedOptions,
//     }));

//     setValue(`options[${index}].options`, updatedOptions);

//     syncOptionImageMap(index, updatedOptions);
//   };

//   /* ---------------- IMAGE DROP ---------------- */

//   const handleDrop = useCallback((files, index) => {
//     const urls = files.map((file) => URL.createObjectURL(file));

//     setImagesPreviewList((prev) => {
//       const updated = {
//         ...prev,
//         [index]: [...(prev[index] || []), ...urls],
//       };

//       setValue(`options[${index}].optionImages`, updated[index]);

//       return updated;
//     });
//   }, []);

//   /* ---------------- INIT ROW ---------------- */

//   const addRow = () => {
//     append({ name: "", options: [], optionImages: [], optionImageMap: {} });
//   };

//   return (
//     <FormProvider {...methods}>
//       <Card>
//         <Card.Body>
//           <Table bordered>
//             <thead>
//               <tr>
//                 <th></th>
//                 <th>Name</th>
//                 <th>Options</th>
//                 <th>Images</th>
//                 <th></th>
//               </tr>
//             </thead>

//             <tbody>
//               {fields.map((item, index) => {
//                 const tags = optionsList[index] || [];
//                 const images = imagesPreviewList[index] || [];
//                 const map = watch(`options[${index}].optionImageMap`) || {};

//                 return (
//                   <React.Fragment key={item.id}>
//                     {/* ================= MAIN ROW ================= */}
//                     <tr>
//                       <td>
//                         <Button
//                           size="sm"
//                           variant="light"
//                           onClick={() => toggleRow(index)}
//                         >
//                           {expandedRows[index] ? "-" : "+"}
//                         </Button>
//                       </td>

//                       <td>
//                         <Form.Control
//                           placeholder="Variant Name"
//                           {...register(`options[${index}].name`)}
//                         />
//                       </td>

//                       <td>
//                         <div className="form-control d-flex flex-wrap">
//                           {tags.map((tag) => (
//                             <span
//                               key={tag}
//                               style={{
//                                 background: "#d9e7fa",
//                                 padding: "2px 6px",
//                                 margin: 2,
//                                 borderRadius: 4,
//                               }}
//                             >
//                               {tag}
//                               <span
//                                 style={{ cursor: "pointer", marginLeft: 4 }}
//                                 onClick={() => handleRemoveTag(index, tag)}
//                               >
//                                 ×
//                               </span>
//                             </span>
//                           ))}
//                           <input
//                             className="border-0 flex-grow-1"
//                             style={{ outline: "none" }}
//                             value={inputValues[index] || ""}
//                             onChange={(e) =>
//                               setInputValues((prev) => ({
//                                 ...prev,
//                                 [index]: e.target.value,
//                               }))
//                             }
//                             onKeyDown={(e) => handleKeyPress(index, e)}
//                             placeholder="Add option..."
//                           />
//                         </div>
//                       </td>

//                       <td>
//                         <Dropzone onDrop={handleDrop} index={index} />

//                         <Row className="g-1 mt-1">
//                           {images.map((img, i) => (
//                             <Col xs={4} key={i}>
//                               <Image src={img} fluid rounded />
//                             </Col>
//                           ))}
//                         </Row>
//                       </td>

//                       <td>
//                         <Button
//                           size="sm"
//                           variant="danger"
//                           onClick={() => remove(index)}
//                         >
//                           Delete
//                         </Button>
//                       </td>
//                     </tr>

//                     {/* ================= SUB ROWS ================= */}
//                     {expandedRows[index] && (
//                       <tr>
//                         <td colSpan={5} style={{ background: "#f8f9fa" }}>
//                           <Table size="sm" bordered className="mb-0">
//                             <thead>
//                               <tr>
//                                 <th>Option</th>
//                                 <th>Link Image</th>
//                                 <th>Preview</th>
//                               </tr>
//                             </thead>
//                             <tbody>
//                               {tags.map((opt) => (
//                                 <tr key={opt}>
//                                   <td>{opt}</td>

//                                   <td>
//                                     <Form.Select
//                                       value={map[opt] || ""}
//                                       onChange={(e) =>
//                                         setValue(
//                                           `options[${index}].optionImageMap.${opt}`,
//                                           e.target.value,
//                                         )
//                                       }
//                                     >
//                                       <option value="">Select Image</option>
//                                       {images.map((img, i) => (
//                                         <option key={i} value={img}>
//                                           Image {i + 1}
//                                         </option>
//                                       ))}
//                                     </Form.Select>
//                                   </td>

//                                   <td>
//                                     {map[opt] && (
//                                       <Image
//                                         src={map[opt]}
//                                         width={40}
//                                         rounded
//                                       />
//                                     )}
//                                   </td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </Table>
//                         </td>
//                       </tr>
//                     )}
//                   </React.Fragment>
//                 );
//               })}
//             </tbody>
//           </Table>

//           <Button onClick={addRow}>➕ Add Variant</Button>

//           {/* Debug payload */}
//           <pre className="mt-3">{JSON.stringify(watch(), null, 2)}</pre>
//         </Card.Body>
//       </Card>
//     </FormProvider>
//   );
// };

// export default VariantsBasicInformation;

// {
//   expandedRows[index] && (
//     <tr>
//       <td colSpan={8} style={{ background: "#f8f9fa" }}>
//         <Table size="sm" bordered className="mb-0">
//           <thead>
//             <tr>
//               <th style={{ width: "25%" }}>Option</th>
//               <th style={{ width: "40%" }}>Link Image</th>
//               <th style={{ width: "30%" }}>Preview</th>
//             </tr>
//           </thead>
//           <tbody>
//             {(optionsList[index] || []).map((opt) => {
//               const map = watch(`options[${index}].optionImageMap`) || {};

//               const images = (imagesList[index] || []).filter(
//                 (img) => img && img !== "" && img.destination !== "null",
//               ); // ✅ filter out empty or invalid images

//               // const previewImages =
//               //   imagesPreviewList[index] || [];
//               // const uploadedImages =
//               //   imagesList[index] || [];

//               // const images =
//               //   previewImages.length > 0
//               //     ? previewImages
//               //     : uploadedImages;
//               // console.log("images: ", images);

//               return (
//                 <tr key={opt}>
//                   <td>{opt}</td>

//                   <td>
//                     <Form.Select
//                       value={map[opt] || ""}
//                       onChange={(e) =>
//                         setValue(
//                           `options[${index}].optionImageMap.${opt}`,
//                           e.target.value,
//                         )
//                       }
//                     >
//                       <option value="">Select Image</option>
//                       {images.map((img, i) => (
//                         <option key={i} value={img}>
//                           Image {i + 1}
//                         </option>
//                       ))}
//                     </Form.Select>
//                   </td>

//                   {/* <td>
//                                         {map[opt] && (
//                                           <Image
//                                             src={map[opt]}
//                                             width={85}
//                                             height={40}
//                                             rounded
//                                           />
//                                         )}
//                                       </td> */}
//                   <td>
//                     {map[opt] &&
//                       (imagesPreviewList[index]?.includes(map[opt]) ||
//                         imagesList[index]?.includes(map[opt])) && (
//                         <Image
//                           // src={map[opt]}
//                           src={`${BASEURL}/wwwroot${map[opt]}`}
//                           width={85}
//                           height={40}
//                           rounded
//                         />
//                       )}
//                   </td>
//                 </tr>
//               );
//             })}
//           </tbody>
//         </Table>
//       </td>
//     </tr>
//   );
// }
